import _ from 'lodash';
import {
  BadgeDollarSignIcon,
  CircleCheckIcon,
  CoinsIcon,
  Truck,
} from 'lucide-react';

import type { QuoteCardType } from 'components/QuoteCard';
import { titleCase } from 'utils/formatStrings';
import { getDrumkitContainer } from 'utils/getDrumkitContainer';
import { cn } from 'utils/shadcn';

import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from './Tooltip';
import { Flex } from './layout';
import { Typography } from './typography';

type QuoteCardTransfixProps = {
  carrier: QuoteCardType;
  isSelected: boolean;
  onClick: () => void;
};

export function QuoteCardTransfix({
  carrier,
  isSelected,
  onClick,
}: QuoteCardTransfixProps) {
  return (
    <TooltipProvider>
      <Flex
        key={carrier.type}
        direction='col'
        className={cn(
          'relative w-full border-2 border-brand-400 rounded-[4px] p-2 transition-transform',
          isSelected
            ? 'bg-brand-50'
            : 'cursor-pointer hover:scale-[1.01] bg-neutral-50'
        )}
        onClick={onClick}
        data-testid='quote-card-transfix'
      >
        <Flex justify='between' align='center' className='w-full mb-2'>
          <Flex direction='col'>
            <Flex align='center' gap='xs'>
              <Typography variant='body-sm' weight='semibold'>
                {carrier.title}
              </Typography>
            </Flex>
          </Flex>
          {carrier.icon}
        </Flex>

        <TransfixDetailSection carrier={carrier} />

        {isSelected && (
          <CircleCheckIcon
            data-testid='quote-card-selected-check'
            className='absolute -top-3 -right-2.5 fill-neutral-50 stroke-success-500! w-6 h-6'
            aria-hidden
          />
        )}
      </Flex>
    </TooltipProvider>
  );
}

function TransfixDetailSection({ carrier }: { carrier: QuoteCardType }) {
  const popoverRootContainer = getDrumkitContainer();

  const roundedCost = _.round(carrier.cost);
  const roundedCostPerMile =
    carrier.costPerMile != null ? _.round(carrier.costPerMile, 2) : null;
  const roundedPriceRange = carrier.priceRange
    ? {
        lowEstimate: _.round(carrier.priceRange.lowEstimate),
        highEstimate: _.round(carrier.priceRange.highEstimate),
      }
    : null;
  const roundedPriceRangePerMile = carrier.priceRangePerMile
    ? {
        lowEstimate: _.round(carrier.priceRangePerMile.lowEstimate, 2),
        highEstimate: _.round(carrier.priceRangePerMile.highEstimate, 2),
      }
    : null;

  return (
    <Flex direction='col' className='w-full gap-0'>
      <Flex justify='between' align='center' className='w-full mt-2'>
        <Flex align='center' className='gap-x-1.5'>
          <Truck className='h-4 w-4' />
          <Typography variant='body-sm'>Transport Type</Typography>
        </Flex>
        {carrier.inputtedTransportType !== carrier.actualTransportType ? (
          <Flex align='baseline' gap='sm'>
            <Tooltip delayDuration={10}>
              <TooltipTrigger className='border-b border-dashed border-neutral-900'>
                <Typography variant='body-sm'>
                  {titleCase(carrier.actualTransportType)}
                </Typography>
              </TooltipTrigger>
              <TooltipPortal container={popoverRootContainer}>
                <TooltipContent className='mr-1 max-w-60 whitespace-pre-wrap'>
                  <Typography variant='body-xs'>
                    {`${carrier.quotingIntegrationName ?? 'Quoting integration'} `}
                    does not support {titleCase(carrier.inputtedTransportType)}{' '}
                    quotes, showing equivalent{' '}
                    {titleCase(carrier.actualTransportType)} quote.
                  </Typography>
                </TooltipContent>
              </TooltipPortal>
            </Tooltip>
          </Flex>
        ) : (
          <Typography variant='body-sm'>
            {titleCase(carrier.actualTransportType)}
          </Typography>
        )}
      </Flex>

      <Flex justify='between' align='center' className='w-full'>
        <Flex align='center' gap='sm'>
          <BadgeDollarSignIcon className='h-4 w-4' />
          <Typography variant='body-sm'>Buy Rate</Typography>
        </Flex>
        <Flex align='baseline' className='gap-x-2 flex-wrap justify-end'>
          <Typography variant='body-xs'>{`$${roundedCost.toLocaleString()}`}</Typography>
          {roundedCostPerMile != null && (
            <Typography variant='body-xs'>{`($${roundedCostPerMile.toLocaleString()}/mi)`}</Typography>
          )}
        </Flex>
      </Flex>

      {roundedPriceRange != null && (
        <Flex justify='between' align='center' className='w-full'>
          <Flex align='center' gap='sm'>
            <CoinsIcon className='h-4 w-4' />
            <Typography variant='body-sm'>Price Range</Typography>
          </Flex>
          <Flex align='center' className='gap-x-2'>
            {roundedPriceRangePerMile ? (
              <Tooltip delayDuration={10}>
                <TooltipTrigger className='border-b border-dashed border-neutral-900'>
                  <Typography variant='body-xs'>
                    {`$${roundedPriceRange.lowEstimate.toLocaleString()} - $${roundedPriceRange.highEstimate.toLocaleString()}`}
                  </Typography>
                </TooltipTrigger>
                <TooltipPortal container={popoverRootContainer}>
                  <TooltipContent className='mr-2 max-w-60 whitespace-pre-wrap'>
                    <div className='pb-3'>
                      <Typography variant='body-xs'>
                        Price range per mile
                      </Typography>
                      <Typography variant='body-xs'>
                        {`$${roundedPriceRangePerMile.lowEstimate.toLocaleString()} - $${roundedPriceRangePerMile.highEstimate.toLocaleString()}/mi.`}
                      </Typography>
                    </div>
                  </TooltipContent>
                </TooltipPortal>
              </Tooltip>
            ) : (
              <Typography variant='body-xs'>
                {`$${roundedPriceRange.lowEstimate.toLocaleString()} - $${roundedPriceRange.highEstimate.toLocaleString()}`}
              </Typography>
            )}
          </Flex>
        </Flex>
      )}
    </Flex>
  );
}
